import {
  ReactNode,
  createContext,
  memo,
  useCallback,
  useContext,
  useState,
} from "react";

const STORAGE_KEY = "redo:phone-widget-hidden";
const EXPIRY_MS = 30 * 24 * 60 * 60 * 1000; // 30 days

export function isPhoneWidgetHiddenByUser(): boolean {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) {
      return false;
    }
    const { value, expiry } = JSON.parse(stored) as {
      value: boolean;
      expiry: number;
    };
    if (Date.now() > expiry) {
      localStorage.removeItem(STORAGE_KEY);
      return false;
    }
    return value;
  } catch {
    return false;
  }
}

function saveHiddenState(hidden: boolean): void {
  try {
    localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ value: hidden, expiry: Date.now() + EXPIRY_MS }),
    );
  } catch {
    // Ignore storage errors (e.g. private browsing quota)
  }
}

interface PhoneWidgetContextType {
  isVisible: boolean;
  toggleVisibility: () => void;
  show: () => void;
  hide: () => void;
  isVoiceAvailable: boolean; // Whether voice features are available
  setVoiceAvailable: (available: boolean) => void; // Function to set voice availability
  forceVoiceAvailable: boolean; // Overrides the "enabled" gate, e.g. from the settings testing tab
  setForceVoiceAvailable: (value: boolean) => void;
}

const PhoneWidgetContext = createContext<PhoneWidgetContextType | null>(null);

export function usePhoneWidget(): PhoneWidgetContextType {
  const context = useContext(PhoneWidgetContext);
  if (!context) {
    throw new Error("usePhoneWidget must be used within a PhoneWidgetProvider");
  }
  return context;
}

interface PhoneWidgetProviderProps {
  children: ReactNode;
}

export const PhoneWidgetProvider = memo(function PhoneWidgetProvider({
  children,
}: PhoneWidgetProviderProps) {
  const [isVisible, setIsVisible] = useState(false);
  const [isVoiceAvailable, setVoiceAvailable] = useState(false);
  const [forceVoiceAvailable, setForceVoiceAvailable] = useState(false);

  const show = useCallback(() => {
    saveHiddenState(false);
    setIsVisible(true);
  }, []);

  const hide = useCallback(() => {
    saveHiddenState(true);
    setIsVisible(false);
  }, []);

  const toggleVisibility = useCallback(
    () => (isVisible ? hide() : show()),
    [isVisible, hide, show],
  );

  const setVoiceAvailableCallback = useCallback(
    (available: boolean) => setVoiceAvailable(available),
    [],
  );

  const setForceVoiceAvailableCallback = useCallback(
    (value: boolean) => setForceVoiceAvailable(value),
    [],
  );

  return (
    <PhoneWidgetContext.Provider
      value={{
        isVisible,
        toggleVisibility,
        show,
        hide,
        isVoiceAvailable,
        setVoiceAvailable: setVoiceAvailableCallback,
        forceVoiceAvailable,
        setForceVoiceAvailable: setForceVoiceAvailableCallback,
      }}
    >
      {children}
    </PhoneWidgetContext.Provider>
  );
});
